import type { ListViewPath } from "_shared/_views/list-pages/current-table-view";
import {
  readListChips,
  writeListChips,
  type ListChipGroup,
  type ListChipSelection,
} from "_shared/_views/list-pages/list-chip-selection";
import type { RetiredListSegment } from "_shared/_views/list-pages/retired-list-redirect";
import type { CurrentTableViewOptions } from "_shared/_views/list-pages/use-current-table-view";
import { PageTab } from "_shared/teams/teams-tabs";
import type { ActiveTeamPerspective } from "work/work-agreements/teams/active-team/_schemas/active-team-schema";

/**
 * The tabs and chips of the Teams page, and how its URL spells them (#6718).
 *
 * The page had four tabs: Templates, Teams I'm On, Teams Working For Me and Archived. Two of them were
 * one list seen from two sides, and one was the archive of another. Now each kind of thing is a tab --
 * Teams and Templates -- and the rest are chips: Teams has **I'm on** and **Working for me**, which
 * combine, and Templates has **Archived**.
 */

/** The path of the page; each tab is a segment under it. */
export const TEAMS_PATH = "/teams";

/** The query parameter of the Teams tab's perspective chips. */
export const PERSPECTIVE_PARAMETER = "perspective";

/** The I'm on chip's value: the teams the reader leads or is on, on either side of the work. */
export const ON_PERSPECTIVE = "on";

/** The Working for me chip's value: the vendor teams on the work the reader leads as the client. */
export const WORKING_FOR_ME_PERSPECTIVE = "working-for-me";

/** The query parameter of the Templates tab's Archived chip. */
export const STAGE_PARAMETER = "stage";

/** The Archived chip's value. */
export const ARCHIVED_STAGE = "archived";

/**
 * The Teams tab's perspective: All, I'm on, or Working for me, a segmented control (#6720). All lists
 * both perspectives, as `GET work/active-teams` does with none (#6570), so a link that names both is
 * All.
 */
export const teamsPerspectiveChipGroup: ListChipGroup = {
  parameter: PERSPECTIVE_PARAMETER,
  label: "Perspective",
  chips: [
    { value: ON_PERSPECTIVE, label: "I'm on" },
    { value: WORKING_FOR_ME_PERSPECTIVE, label: "Working for me" },
  ],
  sameAsNone: [ON_PERSPECTIVE, WORKING_FOR_ME_PERSPECTIVE],
  isSingle: true,
};

/** The Templates tab's chips. */
export const templatesStageChipGroup: ListChipGroup = {
  parameter: STAGE_PARAMETER,
  label: "Stage",
  chips: [{ value: ARCHIVED_STAGE, label: "Archived" }],
};

/** The chip groups of each tab. */
export const teamsChipGroups: Readonly<Record<PageTab, readonly ListChipGroup[]>> = {
  [PageTab.Teams]: [teamsPerspectiveChipGroup],
  [PageTab.Templates]: [templatesStageChipGroup],
};

/** The tab a path segment names; Teams for none, or for one the page does not have. */
export function readTeamsTab(segment: string | undefined): PageTab {
  const named = segment?.toLowerCase();
  return Object.values(PageTab).find((tab) => tab === named) ?? PageTab.Teams;
}

/** The path of `tab`. Both tabs keep a segment, as the page's other routes do: `/teams/templates/edit/:id`. */
export function teamsTabPath(tab: PageTab): string {
  return `${TEAMS_PATH}/${tab}`;
}

/** The chips of `tab` that `searchParams` names. */
export function readTeamsChips(tab: PageTab, searchParams: URLSearchParams): ListChipSelection {
  return readListChips(teamsChipGroups[tab], searchParams);
}

/** `searchParams` with `selection` written over `tab`'s chips. The list starts again at its first page. */
export function writeTeamsChips(
  tab: PageTab,
  selection: ListChipSelection,
  searchParams: URLSearchParams,
): URLSearchParams {
  const next = writeListChips(teamsChipGroups[tab], selection, searchParams);
  if (next.has("pageIndex")) next.set("pageIndex", "0");
  return next;
}

/** Whether `selection` lists the template archive. */
export function listsArchive(selection: ListChipSelection): boolean {
  return (selection[STAGE_PARAMETER] ?? []).includes(ARCHIVED_STAGE);
}

/**
 * The perspective `GET work/active-teams` is asked for: the one chip that is on, or none -- both teams
 * -- when no chip is, which is also what both chips on are spelled as.
 */
export function activeTeamPerspectiveOf(selection: ListChipSelection): ActiveTeamPerspective | undefined {
  const chosen = selection[PERSPECTIVE_PARAMETER] ?? [];
  if (chosen.length !== 1) return undefined;
  return chosen[0] === WORKING_FOR_ME_PERSPECTIVE ? "WorkingForMe" : "On";
}

/** The templates' list: the Templates tab with no chip. */
export const TEMPLATES_PATH = teamsTabPath(PageTab.Templates);

/** The archived templates' list: the Templates tab with its Archived chip. */
export const ARCHIVED_TEMPLATES_PATH = `${TEMPLATES_PATH}?${STAGE_PARAMETER}=${ARCHIVED_STAGE}`;

/**
 * The list tabs the page had before #6718. Bookmarks, notifications and the assistant name them, so each
 * opens the tab and chip it became. `/teams/archived/:action/:id` is another route and keeps resolving.
 */
export const RETIRED_TEAMS_TABS: readonly RetiredListSegment[] = [
  { segment: "archived", tab: PageTab.Templates, chips: { [STAGE_PARAMETER]: [ARCHIVED_STAGE] } },
  { segment: "teams-on", tab: PageTab.Teams, chips: { [PERSPECTIVE_PARAMETER]: [ON_PERSPECTIVE] } },
  {
    segment: "teams-working-for-me",
    tab: PageTab.Teams,
    chips: { [PERSPECTIVE_PARAMETER]: [WORKING_FOR_ME_PERSPECTIVE] },
  },
];

/** The parameters each tab's stored view keeps besides its chips: its sort and page size. */
const KEPT_PARAMETERS: readonly string[] = ["orderBy", "orderDir", "pageSize"];

/**
 * A stored view as this page still reads it: on a tab the page has, with a chip it no longer has
 * dropped. A view of a retired tab is shown as the tab and chips it became.
 */
export function keepTeamsView(view: ListViewPath): ListViewPath | undefined {
  const segment = view.tabPath.startsWith(`${TEAMS_PATH}/`) ? view.tabPath.slice(TEAMS_PATH.length + 1) : undefined;
  if (segment === undefined || segment.includes("/")) return undefined;
  const retired = RETIRED_TEAMS_TABS.find((each) => each.segment === segment);
  const tab = (retired?.tab as PageTab | undefined) ?? Object.values(PageTab).find((each) => each === segment);
  if (tab === undefined) return undefined;

  const stored = new URLSearchParams(view.search);
  const chips = retired?.chips ?? readTeamsChips(tab, stored);
  const kept = writeListChips(teamsChipGroups[tab], chips, new URLSearchParams());
  for (const parameter of KEPT_PARAMETERS) {
    for (const value of stored.getAll(parameter)) kept.append(parameter, value);
  }
  return { tabPath: teamsTabPath(tab), search: kept.toString() };
}

/**
 * The page's Default view, declared once: the Teams tab with no chip, both perspectives. A plain visit
 * to `/teams` opens the reader's starred or last view, else this one.
 */
export const TEAMS_CURRENT_VIEW: CurrentTableViewOptions = {
  tableKey: "teams",
  basePath: TEAMS_PATH,
  defaultHref: teamsTabPath(PageTab.Teams),
  keepWhatStillExists: keepTeamsView,
};
